import React, { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../../../services/api';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useToast } from '../../../../contexts/ToastContext';
import { queryKeys } from '../../../../lib/queryKeys';
import type { StaffAbsenceDto } from '../../../../types/api-responses';
import { resolveWorkingWindow, describeAbsence } from '../../../../lib/staffScheduleHint';
import s from './StaffStatusModal.module.css';

/**
 * AB#2019 (CR-2026-07-19) — at-a-glance staff availability panel launched from
 * the calendar staff dropdown. Read-only: reuses the existing schedule + absence
 * GETs (no new BE) to show, for one staff member at one branch:
 *   • Today  — the working window + any break/leave in effect right now.
 *   • Upcoming — future breaks + leaves/vacations, sorted by start date.
 * So the owner can see when someone is free before booking or granting leave,
 * without walking the calendar day by day.
 */
export interface StaffStatusModalProps {
    staffId: number;
    staffName: string;
    branchId: number;
    /** AB#2021 — edit a today break: hands the absence up so the page opens the editor in edit mode. */
    onEditBreak?: (absence: StaffAbsenceDto) => void;
    onClose: () => void;
}

// AB#2021 — only short intra-day breaks (Break/Personal/Emergency) that are in
// effect TODAY may be edited/removed from this panel; leaves + future rows are read-only.
const INTRA_DAY_NAMES = new Set(['Break', 'PersonalTime', 'Emergency']);

/** Local "yyyy-MM-dd" for today (timezone-safe — no UTC shift). */
const todayIso = (): string => {
    const d = new Date();
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/** Type-name → dot colour class (matches the calendar block palette). */
const DOT_CLASS: Record<string, string> = {
    Break: s.dotBreak,
    PersonalTime: s.dotBreak,
    Emergency: s.dotEmergency,
    SickLeave: s.dotLeave,
    Vacation: s.dotLeave,
};

const StaffStatusModal: React.FC<StaffStatusModalProps> = ({
    staffId, staffName, branchId, onEditBreak, onClose,
}) => {
    const { t } = useLanguage();
    const toast = useToast();
    const queryClient = useQueryClient();
    const today = todayIso();

    // Same query keys as QuickAbsenceModal so the cache is shared (AC-7).
    const { data: schedule, isLoading: schedLoading } = useQuery({
        queryKey: ['staff-schedule', staffId] as const,
        queryFn: () => apiService.getStaffSchedule(staffId),
        staleTime: 60_000,
    });
    const { data: absencesData, isLoading: absLoading } = useQuery({
        queryKey: ['staff-absences', staffId] as const,
        queryFn: () => apiService.getStaffAbsences(staffId),
        staleTime: 30_000,
    });

    const workWindow = useMemo(
        () => resolveWorkingWindow(schedule?.slots, branchId, today),
        [schedule, branchId, today],
    );

    // In effect today: date range covers today. Upcoming: starts after today.
    const { activeToday, upcoming } = useMemo(() => {
        const rows = (absencesData?.absences ?? [])
            .filter(a => a.branchId === branchId);
        const active: StaffAbsenceDto[] = [];
        const later: StaffAbsenceDto[] = [];
        for (const a of rows) {
            if (a.startDate <= today && today <= a.endDate) active.push(a);
            else if (a.startDate > today) later.push(a);
        }
        later.sort((x, y) => (x.startDate < y.startDate ? -1 : x.startDate > y.startDate ? 1 : 0));
        return { activeToday: active, upcoming: later };
    }, [absencesData, branchId, today]);

    const loading = schedLoading || absLoading;

    // AB#2021 — remove a today break (soft-delete). Refresh the panel + calendar.
    const deleteMut = useMutation({
        mutationFn: (absenceId: number) => apiService.deleteStaffAbsence(staffId, absenceId),
        onSuccess: () => {
            toast.showToast(t('staff.absence.toast.deleted'), 'success');
            queryClient.invalidateQueries({ queryKey: ['staff-absences', staffId] });
            queryClient.invalidateQueries({ queryKey: queryKeys.calendar.all });
        },
        onError: () => toast.showToast(t('staff.absence.toast.deleteFailed'), 'error'),
    });

    // A today break is editable/removable here; leaves + future rows are read-only.
    const isEditableBreak = (a: StaffAbsenceDto) =>
        INTRA_DAY_NAMES.has(a.absenceType) && a.startDate <= today && today <= a.endDate;

    const row = (a: StaffAbsenceDto, editable: boolean) => (
        <li key={a.id} className={s.item}>
            <span className={`${s.dot} ${DOT_CLASS[a.absenceType] ?? s.dotBreak}`} aria-hidden="true" />
            <span className={s.itemText}>
                {describeAbsence(a, t)}
                {a.reason ? <span className={s.reason}> · {a.reason}</span> : null}
            </span>
            {editable && (
                <span className={s.rowActions}>
                    {onEditBreak && (
                        <button
                            type="button"
                            className={s.actionBtn}
                            onClick={() => onEditBreak(a)}
                            aria-label={t('staff.status.editBreak')}
                            title={t('staff.status.editBreak')}
                        >✎</button>
                    )}
                    <button
                        type="button"
                        className={`${s.actionBtn} ${s.actionDanger}`}
                        disabled={deleteMut.isPending}
                        onClick={() => {
                            if (window.confirm(t('staff.status.removeConfirm'))) deleteMut.mutate(a.id);
                        }}
                        aria-label={t('staff.status.removeBreak')}
                        title={t('staff.status.removeBreak')}
                    >×</button>
                </span>
            )}
        </li>
    );

    return (
        <div className={s.overlay} onMouseDown={onClose}>
            <div className={s.card} onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                <div className={s.header}>
                    <div>
                        <h2 className={s.title}>{t('staff.status.title')}</h2>
                        <p className={s.subtitle}>{staffName}</p>
                    </div>
                    <button type="button" className={s.close} aria-label={t('staff.status.close')} onClick={onClose}>×</button>
                </div>

                <div className={s.body}>
                    {loading ? (
                        <p className={s.muted}>{t('staff.status.loading')}</p>
                    ) : (
                        <>
                            <section className={s.section}>
                                <h3 className={s.sectionTitle}>{t('staff.status.today')}</h3>
                                <p className={s.workLine}>
                                    {workWindow.kind === 'working'
                                        ? t('staff.status.working').replace('{range}', `⁨${workWindow.start}–${workWindow.end}⁩`)
                                        : t('staff.status.off')}
                                </p>
                                {activeToday.length > 0 && (
                                    <ul className={s.list}>{activeToday.map(a => row(a, isEditableBreak(a)))}</ul>
                                )}
                            </section>

                            <section className={s.section}>
                                <h3 className={s.sectionTitle}>{t('staff.status.upcoming')}</h3>
                                {upcoming.length > 0 ? (
                                    <ul className={s.list}>{upcoming.map(a => row(a, false))}</ul>
                                ) : (
                                    <p className={s.muted}>{t('staff.status.noUpcoming')}</p>
                                )}
                            </section>
                        </>
                    )}
                </div>

                <div className={s.footer}>
                    <button type="button" className={s.closeBtn} onClick={onClose}>
                        {t('staff.status.close')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default StaffStatusModal;
